"use client";

import React, { useState, useRef, useEffect } from "react";
import axios from "@/lib/api";
import toast from "react-hot-toast";
import type { AxiosError } from "axios";

const AddCarPage = () => {
  const [carName, setCarName] = useState("");
  const [model, setModel] = useState("");

  const [transmission, setTransmission] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [features, setFeatures] = useState("");

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [rawImage, setRawImage] = useState<File | null>(null);

  const [galleryImages, setGalleryImages] = useState<File[]>([]);
  const [galleryPreview, setGalleryPreview] = useState<string[]>([]);

  const [rotation, setRotation] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [maskBox, setMaskBox] = useState({
    x: 50,
    y: 50,
    width: 100,
    height: 18,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Cleanup URLs on unmount to prevent memory leaks
  const previewRef = useRef<string | null>(null);
  const galleryRef = useRef<string[]>([]);

  useEffect(() => {
    previewRef.current = preview;
    galleryRef.current = galleryPreview;
  }, [preview, galleryPreview]);

  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
      galleryRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (preview) URL.revokeObjectURL(preview);
      setRawImage(file);
      const url = URL.createObjectURL(file);
      setPreview(url);
    }
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const img = imageRef.current;
    if (!canvas || !ctx || !img || !img.complete) return;

    const maxWidth = 500;
    const ratio = img.naturalWidth / img.naturalHeight;
    const scaledWidth = maxWidth;
    const scaledHeight = scaledWidth / ratio;

    canvas.width = scaledWidth;
    canvas.height = scaledHeight;

    ctx.clearRect(0, 0, scaledWidth, scaledHeight);
    ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);

    const centerX = maskBox.x + maskBox.width / 2;
    const centerY = maskBox.y + maskBox.height / 2;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.fillStyle = "rgba(0, 0, 0, 1)";
    ctx.fillRect(
      -maskBox.width / 2,
      -maskBox.height / 2,
      maskBox.width,
      maskBox.height
    );
    ctx.restore();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (
      x >= maskBox.x &&
      x <= maskBox.x + maskBox.width &&
      y >= maskBox.y &&
      y <= maskBox.y + maskBox.height
    ) {
      setIsDragging(true);
      setOffset({ x: x - maskBox.x, y: y - maskBox.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newX = Math.max(
      0,
      Math.min(x - offset.x, canvasRef.current.width - maskBox.width)
    );
    const newY = Math.max(
      0,
      Math.min(y - offset.y, canvasRef.current.height - maskBox.height)
    );

    setMaskBox((prev) => {
      const updated = { ...prev, x: newX, y: newY };
      setTimeout(drawCanvas, 0);
      return updated;
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!carName.trim()) return toast.error("Car name is required.");
    if (!model.trim()) return toast.error("Model is required.");
    if (!transmission) return toast.error("Transmission is required.");
    if (!fuelType) return toast.error("Fuel type is required.");
    if (!rawImage || !canvasRef.current)
      return toast.error("Please upload a main image and wait for preview.");

    setLoading(true);

    try {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvasRef.current?.toBlob(resolve);
      });
      if (!blob) {
        toast.error("Failed to generate image.");
        return;
      }

      const formData = new FormData();
      formData.append("name", carName.trim());
      formData.append("model", model.trim());
      formData.append("status", "Available");
      formData.append("image", blob, rawImage.name);
      formData.append("transmission", transmission);
      formData.append("fuelType", fuelType);
      formData.append("features", features.trim());

      galleryImages.forEach((file) => formData.append("gallery", file));

      await axios.post("/cars", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Car added successfully!");

      // Reset
      setCarName("");
      setModel("");
      setTransmission("");
      setFuelType("");
      setFeatures("");
      setRawImage(null);
      setPreview(null);
      setGalleryImages([]);
      setGalleryPreview([]);
      setRotation(0);
      setMaskBox({ x: 50, y: 50, width: 100, height: 18 });
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      const errorMessage =
        axiosError.response?.data?.message ||
        "Something went wrong while adding the car.";
      console.error("Add Car Error:", axiosError);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const removeGalleryImage = (idx: number) => {
    const updatedPreview = [...galleryPreview];
    const updatedImages = [...galleryImages];
    URL.revokeObjectURL(updatedPreview[idx]);
    updatedPreview.splice(idx, 1);
    updatedImages.splice(idx, 1);
    setGalleryPreview(updatedPreview);
    setGalleryImages(updatedImages);
  };

  const addGalleryImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setGalleryImages((prev) => [...prev, file]);
      setGalleryPreview((prev) => [...prev, URL.createObjectURL(file)]);
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-2xl p-6 mx-auto">
      <form
        onSubmit={handleSubmit}
        className="p-6 space-y-4 bg-white rounded shadow"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-blue-900">
              Car Name *
            </label>
            <input
              type="text"
              value={carName}
              onChange={(e) => setCarName(e.target.value)}
              required
              className="w-full px-3 py-2 mt-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-blue-900">
              Model *
            </label>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              required
              className="w-full px-3 py-2 mt-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-blue-900">
              Transmission *
            </label>
            <select
              value={transmission}
              className="w-full px-3 py-2 mt-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              onChange={(e) => setTransmission(e.target.value)}
            >
              <option value="">Select...</option>
              <option value="Automatic">Automatic</option>
              <option value="Manual">Manual</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-blue-900">
              Fuel Type *
            </label>
            <select
              value={fuelType}
              className="w-full px-3 py-2 mt-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              onChange={(e) => setFuelType(e.target.value)}
            >
              <option value="">Select...</option>
              <option value="Petrol">Petrol</option>
              <option value="Diesel">Diesel</option>
            </select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-semibold text-blue-900">
              Features (comma-separated)
            </label>
            <input
              type="text"
              value={features}
              placeholder="e.g., Air Conditioning, Power Steering, ABS"
              className="w-full px-3 py-2 mt-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => setFeatures(e.target.value)}
            />
          </div>
        </div>

        {/* Cover image input */}
        <div className="mt-4">
          <label className="block text-sm font-semibold text-blue-900">
            Main Image (with mask) *
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            required={!rawImage}
            className="mt-1"
          />
        </div>

        {/* Gallery */}
        <div className="mt-4">
          <label className="block mb-2 text-sm font-semibold text-blue-900">
            Gallery Images
          </label>
          <div className="flex flex-wrap gap-2">
            {galleryPreview.map((src, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={src}
                  alt={`gallery-${idx}`}
                  className="object-cover w-24 h-16 border rounded shadow"
                />
                <button
                  type="button"
                  className="absolute top-0 right-0 px-1 text-xs text-white transition bg-red-500 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-600"
                  onClick={() => removeGalleryImage(idx)}
                >
                  ✕
                </button>
              </div>
            ))}
            <label className="flex items-center justify-center w-24 h-16 transition-colors border-2 border-dashed rounded cursor-pointer hover:border-blue-600">
              <span className="text-2xl text-blue-900">+</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={addGalleryImage}
              />
            </label>
          </div>
        </div>

        {/* Canvas masking section */}
        {preview && (
          <>
            <div
              className="relative mt-4 overflow-auto border rounded shadow cursor-grab active:cursor-grabbing"
              style={{ maxHeight: "400px" }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <canvas
                ref={canvasRef}
                className="w-full h-auto"
                style={{ display: "block", margin: "auto" }}
              />
              <img
                ref={imageRef}
                src={preview}
                alt="preview"
                onLoad={drawCanvas}
                className="hidden"
              />
            </div>

            <div className="flex items-center gap-2 mt-3">
              <label className="text-sm font-medium">Rotate:</label>
              <input
                type="range"
                min={-90}
                max={90}
                value={rotation}
                onChange={(e) => {
                  setRotation(Number(e.target.value));
                  setTimeout(drawCanvas, 0);
                }}
                className="flex-1"
              />
              <span className="text-sm font-medium">{rotation}°</span>
            </div>
          </>
        )}

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 font-semibold text-white transition-colors bg-blue-900 rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Uploading..." : "Add Car"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCarPage;
